"use client";
import React, { useEffect, useState, useCallback, useRef } from "react";
import Image from "next/image";
import { useSelector } from "react-redux";
import IMAGES from "@/data/assetData";
import { useDispatch } from "react-redux";
import {
  getAllVehicles,
  setDuration,
  setEmptyCartData,
  getEndDate,
  getTempDateTime,
  setIsSearching,
  setSearchAreas,
  setSelectedAreas,
  setSearchBrands,
  setSelectedBrands,
  setSearchFuelTypes,
  setSelectedFuelTypes,
  setSearchTransmissions,
  setSelectedTransmissions,
  setSortBy,
} from "@/redux/search/searchSlice";
import { usefetchDataFromApi } from "@/utils/api";
import { getFinalVehicleList } from "@/data/staticMethod";
import { getCurrentPosition } from "@/utils/locationHelper";
import { getCustomerPosition } from "@/utils/customerPosition";
import SearchFormMobile from "../SearchForm/SearchFormMobile";
import { usePartner, withPartnerFilter } from "@/utils/partner/PartnerContext";
import { longTermDuration } from "@/data/footerLinkData";

import dayjs from "dayjs";
import { bumpCartVersion } from "@/redux/order/orderSlice";
import { tokens } from "@/styles/tokens";


const NAV_ITEMS = [
  {
    name: "Scooter",
    active: IMAGES.activeScooter,
    inactive: IMAGES.inactiveScooter,
    types: "scooter",
  },
  {
    name: "Motorbike",
    active: IMAGES.activeMoterBike,
    inactive: IMAGES.inactiveMoterBike,
    types: "motorbike",
  },
  {
    name: "Car",
    active: IMAGES.activeCar,
    inactive: IMAGES.inactiveCar,
    types: "car",
  },
  {
    name: "E-Scooter",
    active: IMAGES.activeElectScooter,
    inactive: IMAGES.inactiveElectScooter,
    types: "electric scooter",
  },
];

const NavigationBar = () => {
  const dispatch = useDispatch();

  const selectorData = useSelector((state) => state.search);

  // Active partner storefront (null on global sukuto.com). Every search/index
  // re-fetch below is scoped to the partner so results never leak global
  // inventory into a white-label storefront.
  const partner = usePartner();

  const [multiSelect, setMultiSelect] = useState(
    Array.isArray(selectorData.vehicleType) ? selectorData.vehicleType : ["bike", "motorbike", "electric scooter", "electric bike", "scooter", "car"]
  );

  const filterRowRef = useRef(null);
  // Bumped once a customer position becomes known, so the search below re-runs
  // and comes back with road distances attached.
  const [positionTick, setPositionTick] = useState(0);

  const [compactFilterVisible, setCompactFilterVisible] = useState(false);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [pendingAreas, setPendingAreas] = useState([]);
  const [pendingBrands, setPendingBrands] = useState([]);
  const [pendingFuelTypes, setPendingFuelTypes] = useState([]);
  const [pendingTransmissions, setPendingTransmissions] = useState([]);
  const [pendingSortBy, setPendingSortBy] = useState("");
  const [collapsedSections, setCollapsedSections] = useState({});
  const [bodyScrolled, setBodyScrolled] = useState(false);
  const filterBodyRef = useRef(null);

  const toggleSection = (key) => setCollapsedSections((prev) => ({ ...prev, [key]: !prev[key] }));

  const vehicleCount = selectorData?.searchVehicles?.length || 0;

  useEffect(() => {
    const el = filterRowRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setCompactFilterVisible(!entry.isIntersecting);
      },
      { threshold: 0, rootMargin: "-60px 0px 0px 0px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const scrollToFilterRow = useCallback(() => {
    filterRowRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const {
    searchAreas = [], selectedAreas = [],
    searchBrands = [], selectedBrands = [],
    searchFuelTypes = [], selectedFuelTypes = [],
    searchTransmissions = [], selectedTransmissions = [],
    sortBy = "",
  } = selectorData;

  const totalActiveFilters = selectedAreas.length + selectedBrands.length + selectedFuelTypes.length + selectedTransmissions.length + (sortBy ? 1 : 0);

  const dispatchFilters = (filters) => {
    dispatch(setSearchAreas(filters?.areas || []));
    dispatch(setSearchBrands(filters?.brands || []));
    dispatch(setSearchFuelTypes(filters?.fuelTypes || []));
    dispatch(setSearchTransmissions(filters?.transmissions || []));
  };

  const clearFilters = () => {
    dispatch(setSearchAreas([]));
    dispatch(setSearchBrands([]));
    dispatch(setSearchFuelTypes([]));
    dispatch(setSearchTransmissions([]));
  };

  // Drops what the user has *selected* (not the facet lists above). Changing the
  // category, dates or city invalidates those selections — area ids belong to the
  // old city, brands/fuel/transmission to the old vehicle types — and the re-fetch
  // for the new params deliberately runs unfiltered. Without this the pills and the
  // Filters badge keep advertising a filter that is no longer applied to the
  // results. Sort goes too: the request carries no `sort`, so keeping "Price up"
  // lit would be the same lie, and adding sort to only this payload would break the
  // search/index dedup in utils/api.jsx, which keys on the exact JSON body.
  const clearSelectedFilters = () => {
    dispatch(setSelectedAreas([]));
    dispatch(setSelectedBrands([]));
    dispatch(setSelectedFuelTypes([]));
    dispatch(setSelectedTransmissions([]));
    dispatch(setSortBy(""));
  };

  // Remove a single active filter pill and re-fire API
  const removeFilterPill = (type, value) => {
    let newAreas = [...selectedAreas];
    let newBrands = [...selectedBrands];
    let newFuelTypes = [...selectedFuelTypes];
    let newTransmissions = [...selectedTransmissions];
    let newSort = sortBy;

    if (type === "area") newAreas = newAreas.filter((id) => id !== value);
    else if (type === "brand") newBrands = newBrands.filter((b) => b !== value);
    else if (type === "fuel") newFuelTypes = newFuelTypes.filter((f) => f !== value);
    else if (type === "trans") newTransmissions = newTransmissions.filter((t) => t !== value);
    else if (type === "sort") newSort = "";

    dispatch(setSelectedAreas(newAreas));
    dispatch(setSelectedBrands(newBrands));
    dispatch(setSelectedFuelTypes(newFuelTypes));
    dispatch(setSelectedTransmissions(newTransmissions));
    dispatch(setSortBy(newSort));

    const startDTime = `${selectorData.startDateTime.date} ${selectorData.startDateTime.time}`;
    const endDTime = `${selectorData.endDateTime.date} ${selectorData.endDateTime.time}`;
    let apiData = {
      types: multiSelect.filter((preState) => preState != "bike"),
      start: startDTime, end: endDTime,
      cityId: selectorData.city.id, platform: 3,
    };
    if (selectorData?.searchType == "/monthly-bike-rental") apiData.longRentStatus = 1;
    const filters = {};
    if (newAreas.length > 0) filters.areas = newAreas;
    if (newBrands.length > 0) filters.brands = newBrands;
    if (newFuelTypes.length > 0) filters.fuelTypes = newFuelTypes;
    if (newTransmissions.length > 0) filters.transmissions = newTransmissions;
    if (Object.keys(filters).length > 0) apiData.filters = filters;
    if (newSort) apiData.sort = newSort;

    dispatch(setIsSearching(true));
    usefetchDataFromApi({ url: "search/index", method: "POST", data: withPartnerFilter(apiData, partner?.id) }).then((res) => {
      if (res.success) {
        const { vehicles, filters: respFilters } = res.data?.data || {};
        dispatch(getAllVehicles(getFinalVehicleList(vehicles, startDTime, endDTime)));
        dispatchFilters(respFilters);
      } else {
        dispatch(getAllVehicles([]));
      }
      dispatch(setIsSearching(false));
    });
  };

  const handleCategoryFilter = (item) => {
    if (!multiSelect.includes(item.types)) {
      setMultiSelect([...multiSelect, item.types]);
    } else {
      setMultiSelect(multiSelect.filter((cat) => cat !== item.types));
    }
  };

  const handleDuratnSelect = (data) => {
    if (selectorData.duration.id == data.id) {
      dispatch(setDuration({ id: data.id, value: data.value }));
    } else {
      dispatch(setDuration({ id: data.id, value: data.value }));
      dispatch(setEmptyCartData(true));
    }
  };

  useEffect(() => {
    if (selectorData?.searchType !== "/monthly-bike-rental") return;
    const duratnArr = selectorData?.duration?.value?.split(" ");
    if (!duratnArr || duratnArr.length !== 2) return;
    if (!selectorData?.startDateTime?.date || !selectorData?.startDateTime?.time) return;

    const newEndDate = dayjs(selectorData.startDateTime.date)
      .add(Number(duratnArr[0]), String(duratnArr[1]))
      .format("YYYY-MM-DD");
    const newEndTime = selectorData.startDateTime.time;

    if (selectorData?.endDateTime?.date !== newEndDate) {
      dispatch(getEndDate({ date: newEndDate }));
    }
    if (selectorData?.endDateTime?.time !== newEndTime) {
      dispatch(getEndDate({ time: newEndTime }));
    }
  }, [
    selectorData?.searchType,
    selectorData?.duration?.value,
    selectorData?.startDateTime?.date,
    selectorData?.startDateTime?.time,
    selectorData?.endDateTime?.date,
    selectorData?.endDateTime?.time,
    dispatch,
  ]);



  useEffect(() => {
    const {
      startDateTime,
      endDateTime,
      tempStartDateTime,
      tempEndDateTime,
      tempCityId,
      city,
    } = selectorData;

    if (!tempStartDateTime || !tempEndDateTime || !tempCityId) return;

    const startDTime = `${startDateTime?.date} ${startDateTime?.time}`;
    const endDTime = `${endDateTime?.date} ${endDateTime?.time}`;

    if (
      startDTime === tempStartDateTime ||
      endDTime === tempEndDateTime ||
      city?.id === tempCityId
    ) return;

    usefetchDataFromApi({
      url: "cart/cart-empty",
      method: "POST",
    })
      .then((res) => {
        if (res.success) {
          dispatch(bumpCartVersion());
          dispatch(
            getTempDateTime({
              sDateTime: "",
              eDateTime: "",
              tCityId: "",
            })
          );
        }
      })
      .catch(() => {});
  }, [
    selectorData?.city?.id,
    selectorData?.startDateTime?.date,
    selectorData?.startDateTime?.time,
    selectorData?.endDateTime?.date,
    selectorData?.endDateTime?.time,
    selectorData?.tempStartDateTime,
    selectorData?.tempEndDateTime,
    selectorData?.tempCityId,
    dispatch,
  ]);

  const lastSearchKeyRef = useRef("");

  // Make sure we know where the customer is, so each pickup point can be
  // labelled with its road distance.
  //
  // The home search form asks for location to pick a city, but someone landing
  // straight on /search — or arriving from an SEO city page — never triggers
  // that, so no position was ever captured and the search went out without one.
  // Asking here closes that gap. When permission has already been granted this
  // resolves silently with no prompt, which is the common case.
  //
  // Failure is silent on purpose: a declined or unavailable position just means
  // no distances, which is exactly how the page behaved before.
  useEffect(() => {
    if (getCustomerPosition()) return;
    let cancelled = false;
    getCurrentPosition()
      .then(() => {
        if (cancelled) return;
        // Clear the de-dup guard, otherwise the effect below sees the same
        // search key and returns early — the coordinates would sit in storage
        // and never reach a request until the customer changed something.
        lastSearchKeyRef.current = "";
        setPositionTick((n) => n + 1);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  useEffect(() => {
    const {
      startDateTime,
      endDateTime,
      city,
      searchType,
      duration,
    } = selectorData;

    const types = multiSelect.filter((preState) => preState !== "bike");

    let effectiveEndDate = endDateTime?.date;
    let effectiveEndTime = endDateTime?.time;
    if (searchType === "/monthly-bike-rental" && duration?.value && startDateTime?.date && startDateTime?.time) {
      const duratnArr = duration.value.split(" ");
      if (duratnArr.length === 2) {
        effectiveEndDate = dayjs(startDateTime.date)
          .add(Number(duratnArr[0]), String(duratnArr[1]))
          .format("YYYY-MM-DD");
        effectiveEndTime = startDateTime.time;
      }
    }

    if (
      !city?.id ||
      !startDateTime?.date || !startDateTime?.time ||
      !effectiveEndDate || !effectiveEndTime ||
      types.length === 0
    ) {
      return;
    }

    const startDTime = `${startDateTime.date} ${startDateTime.time}`;
    const endDTime = `${effectiveEndDate} ${effectiveEndTime}`;
    const apiData = {
      types,
      start: startDTime,
      end: endDTime,
      cityId: city.id,
      platform: 3,
    };
    if (searchType === "/monthly-bike-rental") apiData.longRentStatus = 1;

    const searchKey = JSON.stringify(apiData);
    if (searchKey === lastSearchKeyRef.current) return;
    lastSearchKeyRef.current = searchKey;

    if (totalActiveFilters > 0) clearSelectedFilters();

    dispatch(setIsSearching(true));
    usefetchDataFromApi({
      url: "search/index",
      method: "POST",
      data: withPartnerFilter(apiData, partner?.id),
    }).then((res) => {
      if (res.success) {
        const { vehicles, filters } = res.data?.data || {};
        const finalVehicleList = getFinalVehicleList(vehicles, startDTime, endDTime);
        dispatch(getAllVehicles(finalVehicleList));
        dispatchFilters(filters);
      } else {
        dispatch(getAllVehicles([]));
        clearFilters();
      }
      dispatch(setIsSearching(false));
    });
  }, [
    multiSelect,
    selectorData?.startDateTime?.date,
    selectorData?.startDateTime?.time,
    selectorData?.endDateTime?.date,
    selectorData?.endDateTime?.time,
    selectorData?.city?.id,
    selectorData?.searchType,
    selectorData?.duration?.value,
    partner?.id,
    positionTick,
    dispatch,
  ]);

  return (
    <>
      <div className="lg:hidden sticky top-[14px] bg-white z-10 pt-2 pb-1 px-1 flex items-center">
        <div className="flex-1 min-w-0">
          <SearchFormMobile type="search" />
        </div>
        <div
          className={`h-10 flex items-center justify-center overflow-hidden shrink-0 cursor-pointer transition-all duration-300 ease-[cubic-bezier(0.22,0.61,0.36,1)]
            ${compactFilterVisible ? 'w-10 opacity-100 scale-100 pointer-events-auto' : 'w-0 opacity-0 scale-[0.45,0.18] translate-x-[-10px] translate-y-[14px] pointer-events-none'}`}
          onClick={scrollToFilterRow}
          title="Filters"
        >
          <svg className="text-primary" width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      </div>

      <nav ref={filterRowRef} aria-label="Search results filters"
        className={`flex items-center py-1.5 px-1 bg-white overflow-x-auto overflow-y-visible scrollbar-none [-webkit-overflow-scrolling:touch]
          lg:sticky lg:z-10 lg:px-[4%] lg:justify-center lg:gap-1.5 lg:overflow-x-visible lg:flex-wrap
          lg:top-[74px]`}
      >
        {NAV_ITEMS.map((item) => {
          const isActive = multiSelect.includes(item.types);
          return (
            <div
              role="button"
              tabIndex={0}
              aria-pressed={isActive}
              aria-label={item.name}
              onClick={() => handleCategoryFilter(item)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleCategoryFilter(item); } }}
              key={item.types}
              className={`flex flex-col items-center justify-center w-[72px] min-w-[72px] cursor-pointer gap-0.5 ml-1.5 first:ml-0 [-webkit-tap-highlight-color:transparent]
                lg:flex-row lg:w-auto lg:min-w-0 lg:gap-1 lg:py-[5px] lg:px-3 lg:rounded-pill lg:border lg:ml-0
                ${isActive
                  ? 'lg:border-primary lg:bg-redSurface-50'
                  : 'lg:border-gray-200 lg:bg-bgPage lg:hover:border-gray-300 lg:hover:bg-gray-100'
                }
                transition-[border-color,background] duration-[0.18s] ease-in-out`}
            >
              <div className={`relative w-[44px] h-[44px] rounded-full flex items-center justify-center border-[1.2px] shadow-[0_3px_8px_rgba(0,0,0,0.03)] transition-[border-color] duration-[0.18s]
                lg:w-7 lg:h-7 lg:shadow-none lg:border-0 lg:bg-transparent
                ${isActive ? 'border-primary bg-white' : 'border-transparent bg-white'}`}>
                <Image
                  src={isActive ? item.active : item.inactive}
                  alt={item.name}
                  width={24}
                  height={24}
                  className="w-6 h-6 object-contain"
                />
                {isActive && (
                  <span className="absolute -top-0.5 -right-0.5 w-[18px] h-[18px] rounded-full bg-primary border-2 border-white flex items-center justify-center
                    lg:w-3 lg:h-3 lg:-top-0.5 lg:-right-0.5 lg:border-[1.5px]">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                      <path d="M20 6L9 17l-5-5" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </span>
                )}
              </div>
              <span className={`text-xs font-medium whitespace-nowrap overflow-hidden text-ellipsis max-w-[72px] text-center leading-tight
                lg:max-w-none lg:overflow-visible
                ${isActive ? 'font-bold text-primary' : 'text-textDisabledCool'}`}>{item.name}</span>
            </div>
          );
        })}
        <div
          role="button"
          tabIndex={0}
          aria-pressed={totalActiveFilters > 0}
          aria-label="Filters"
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.currentTarget.click(); } }}
          className={`flex flex-col items-center justify-center w-[72px] min-w-[72px] cursor-pointer gap-0.5 ml-1.5 [-webkit-tap-highlight-color:transparent]
            lg:flex-row lg:w-auto lg:min-w-0 lg:gap-1 lg:py-[5px] lg:px-3 lg:rounded-pill lg:border lg:ml-0
            ${totalActiveFilters > 0
              ? 'lg:border-primary lg:bg-redSurface-50'
              : 'lg:border-gray-200 lg:bg-bgPage lg:hover:border-gray-300 lg:hover:bg-gray-100'
            }
            transition-[border-color,background] duration-[0.18s] ease-in-out`}
          onClick={() => {
            if (!showFilterPanel) {
              setPendingAreas([...selectedAreas]);
              setPendingBrands([...selectedBrands]);
              setPendingFuelTypes([...selectedFuelTypes]);
              setPendingTransmissions([...selectedTransmissions]);
              setPendingSortBy(sortBy);
            }
            setShowFilterPanel(!showFilterPanel);
          }}
        >
          <div className={`relative w-[44px] h-[44px] rounded-full flex items-center justify-center border-[1.2px] shadow-[0_3px_8px_rgba(0,0,0,0.03)] transition-[border-color] duration-[0.18s]
            lg:w-7 lg:h-7 lg:shadow-none lg:border-0 lg:bg-transparent
            ${totalActiveFilters > 0 ? 'border-primary bg-white' : 'bg-[#FCFCFD] border-[#F1F5F9] shadow-none lg:bg-transparent lg:border-0'}`}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6" stroke={totalActiveFilters > 0 ? tokens.primary : tokens.textPlaceholder} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {totalActiveFilters > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-[18px] h-[18px] rounded-full bg-primary border-2 border-white flex items-center justify-center
                lg:w-3 lg:h-3 lg:-top-0.5 lg:-right-0.5 lg:border-[1.5px]">
                <span style={{ color: "#fff", fontSize: "9px", fontWeight: 700, lineHeight: 1 }}>{totalActiveFilters}</span>
              </span>
            )}
          </div>
          <span className={`text-xs font-medium whitespace-nowrap overflow-hidden text-ellipsis max-w-[72px] text-center leading-tight
            lg:max-w-none lg:overflow-visible
            ${totalActiveFilters > 0 ? 'font-bold text-primary' : 'text-textDisabledCool'}`}>Filters</span>
        </div>
      </nav>

      {/* Active filter pills */}
      {totalActiveFilters > 0 && (
        <div className="flex flex-wrap gap-1.5 px-2 pt-1.5 pb-0.5">
          {sortBy && (
            <button className="inline-flex items-center gap-1 py-1 px-2.5 rounded-2xl bg-redSurface-50 text-primary border border-redSurface-200 text-xs font-medium cursor-pointer transition-[background,border-color] duration-150 [-webkit-tap-highlight-color:transparent] hover:bg-redSurface-100 hover:border-primary" onClick={() => removeFilterPill("sort", sortBy)}>
              {sortBy === "price_ascending" ? "Price ↑" : "Price ↓"}
              <svg className="shrink-0" width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/></svg>
            </button>
          )}
          {selectedBrands.map((b) => (
            <button key={b} className="inline-flex items-center gap-1 py-1 px-2.5 rounded-2xl bg-redSurface-50 text-primary border border-redSurface-200 text-xs font-medium cursor-pointer transition-[background,border-color] duration-150 [-webkit-tap-highlight-color:transparent] hover:bg-redSurface-100 hover:border-primary" onClick={() => removeFilterPill("brand", b)}>
              {b}
              <svg className="shrink-0" width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/></svg>
            </button>
          ))}
          {selectedFuelTypes.map((f) => (
            <button key={f} className="inline-flex items-center gap-1 py-1 px-2.5 rounded-2xl bg-redSurface-50 text-primary border border-redSurface-200 text-xs font-medium cursor-pointer transition-[background,border-color] duration-150 [-webkit-tap-highlight-color:transparent] hover:bg-redSurface-100 hover:border-primary" onClick={() => removeFilterPill("fuel", f)}>
              {f}
              <svg className="shrink-0" width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/></svg>
            </button>
          ))}
          {selectedTransmissions.map((t) => (
            <button key={t} className="inline-flex items-center gap-1 py-1 px-2.5 rounded-2xl bg-redSurface-50 text-primary border border-redSurface-200 text-xs font-medium cursor-pointer transition-[background,border-color] duration-150 [-webkit-tap-highlight-color:transparent] hover:bg-redSurface-100 hover:border-primary" onClick={() => removeFilterPill("trans", t)}>
              {t}
              <svg className="shrink-0" width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/></svg>
            </button>
          ))}
          {selectedAreas.map((areaId) => {
            const area = searchAreas.find((a) => a.id === areaId);
            return (
              <button key={areaId} className="inline-flex items-center gap-1 py-1 px-2.5 rounded-2xl bg-redSurface-50 text-primary border border-redSurface-200 text-xs font-medium cursor-pointer transition-[background,border-color] duration-150 [-webkit-tap-highlight-color:transparent] hover:bg-redSurface-100 hover:border-primary" onClick={() => removeFilterPill("area", areaId)}>
                {area?.name || areaId}
                <svg className="shrink-0" width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/></svg>
              </button>
            );
          })}
        </div>
      )}

      {/* Disclaimer below the filter bar — shown on all screens */}
      <p className="px-[4%] pt-2 text-[12px] text-gray-500 leading-snug text-center">
        *Prices are inclusive of taxes. Images are used for representation purposes only, actual color may vary.
      </p>

      {showFilterPanel && (
        <>
          <div className="fixed inset-0 bg-black/35 z-50 animate-[fp-fade-in]" onClick={() => setShowFilterPanel(false)} />
          <div className={`fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl z-[51] max-h-[75vh] flex flex-col animate-fp-slide-up
            lg:bottom-0 lg:left-auto lg:right-0 lg:rounded-t-none lg:rounded-tl-2xl lg:w-[360px] lg:shadow-[-8px_0_32px_rgba(0,0,0,0.12)] lg:animate-fp-slide-right
            lg:top-[74px] lg:max-h-[calc(100vh-74px)]`}>
            <div className="flex items-center justify-between px-4 pt-3.5 pb-2.5 border-b border-gray-100">
              <span className="text-base font-semibold text-textPrimary">Filters</span>
              <button className="bg-none border-none cursor-pointer p-1 flex items-center justify-center rounded-full transition-[background] duration-150 hover:bg-gray-100" onClick={() => setShowFilterPanel(false)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6l12 12" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            <div
              className="px-4 pt-3 pb-5 overflow-y-auto [-webkit-overflow-scrolling:touch]"
              ref={filterBodyRef}
              onScroll={(e) => setBodyScrolled(e.target.scrollTop > 4)}
            >

              {/* 1. Sort By (Low/High) */}
              <div className="mb-4 last:mb-0">
                <button className="flex items-center justify-between w-full bg-none border-none cursor-pointer pb-2 [-webkit-tap-highlight-color:transparent]" onClick={() => toggleSection("sort")}>
                  <h4 className="text-[13px] font-semibold text-gray-500 uppercase tracking-[0.4px] m-0">Sort By</h4>
                  <svg className={`shrink-0 transition-transform duration-200 ${collapsedSections.sort ? '-rotate-90' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M6 9l6 6 6-6" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </button>
                {!collapsedSections.sort && (
                  <div className="flex flex-wrap gap-2">
                    {[
                      { value: "", label: "Default", icon: null },
                      { value: "price_ascending", label: "Low → High", icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M12 19V5M5 12l7-7 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg> },
                      { value: "price_descending", label: "High → Low", icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M12 5v14M19 12l-7 7-7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg> },
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        className={`inline-flex items-center gap-1 py-1.5 px-3.5 rounded-card-lg border text-[13px] font-medium whitespace-nowrap cursor-pointer transition-[border-color,background,color] duration-[0.18s] [-webkit-tap-highlight-color:transparent]
                          ${pendingSortBy === opt.value
                            ? 'border-primary bg-redSurface-50 text-primary font-semibold'
                            : 'border-gray-200 bg-bgPage text-gray-500 hover:border-gray-300 hover:bg-gray-100'}`}
                        onClick={() => setPendingSortBy(opt.value)}
                      >
                        {opt.icon}{opt.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* 2. Area */}
              {searchAreas.length > 0 && (
                <div className="mb-4 last:mb-0">
                  <button className="flex items-center justify-between w-full bg-none border-none cursor-pointer pb-2 [-webkit-tap-highlight-color:transparent]" onClick={() => toggleSection("area")}>
                    <h4 className="text-[13px] font-semibold text-gray-500 uppercase tracking-[0.4px] m-0">Area</h4>
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] font-medium text-primary cursor-pointer hover:underline" onClick={(e) => { e.stopPropagation(); setPendingAreas(pendingAreas.length === searchAreas.length ? [] : searchAreas.map((a) => a.id)); }}>
                        {pendingAreas.length === searchAreas.length ? "Deselect All" : "Select All"}
                      </span>
                      <svg className={`shrink-0 transition-transform duration-200 ${collapsedSections.area ? '-rotate-90' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M6 9l6 6 6-6" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </div>
                  </button>
                  {!collapsedSections.area && (
                    <div className="flex flex-wrap gap-2">
                      {searchAreas.map((area) => (
                        <button key={area.id} className={`inline-flex items-center gap-1 py-1.5 px-3.5 rounded-card-lg border text-[13px] font-medium whitespace-nowrap cursor-pointer transition-[border-color,background,color] duration-[0.18s] [-webkit-tap-highlight-color:transparent]
                          ${pendingAreas.includes(area.id) ? 'border-primary bg-redSurface-50 text-primary font-semibold' : 'border-gray-200 bg-bgPage text-gray-500 hover:border-gray-300 hover:bg-gray-100'}`} onClick={() => setPendingAreas((prev) => prev.includes(area.id) ? prev.filter((id) => id !== area.id) : [...prev, area.id])}>
                          {area.name}<span className={`text-[11px] font-semibold rounded-card py-px px-1.5 min-w-[20px] text-center leading-[1.4] ${pendingAreas.includes(area.id) ? 'text-primary bg-redSurface-100' : 'text-gray-400 bg-gray-100'}`}>{area.count}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* 3. Transmission */}
              {searchTransmissions.length > 0 && (
                <div className="mb-4 last:mb-0">
                  <button className="flex items-center justify-between w-full bg-none border-none cursor-pointer pb-2 [-webkit-tap-highlight-color:transparent]" onClick={() => toggleSection("trans")}>
                    <h4 className="text-[13px] font-semibold text-gray-500 uppercase tracking-[0.4px] m-0">Transmission</h4>
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] font-medium text-primary cursor-pointer hover:underline" onClick={(e) => { e.stopPropagation(); setPendingTransmissions(pendingTransmissions.length === searchTransmissions.length ? [] : searchTransmissions.map((t) => t.name)); }}>
                        {pendingTransmissions.length === searchTransmissions.length ? "Deselect All" : "Select All"}
                      </span>
                      <svg className={`shrink-0 transition-transform duration-200 ${collapsedSections.trans ? '-rotate-90' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M6 9l6 6 6-6" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </div>
                  </button>
                  {!collapsedSections.trans && (
                    <div className="flex flex-wrap gap-2">
                      {searchTransmissions.map((trans) => (
                        <button key={trans.name} className={`inline-flex items-center gap-1 py-1.5 px-3.5 rounded-card-lg border text-[13px] font-medium whitespace-nowrap cursor-pointer transition-[border-color,background,color] duration-[0.18s] [-webkit-tap-highlight-color:transparent]
                          ${pendingTransmissions.includes(trans.name) ? 'border-primary bg-redSurface-50 text-primary font-semibold' : 'border-gray-200 bg-bgPage text-gray-500 hover:border-gray-300 hover:bg-gray-100'}`} onClick={() => setPendingTransmissions((prev) => prev.includes(trans.name) ? prev.filter((t) => t !== trans.name) : [...prev, trans.name])}>
                          {trans.name}<span className={`text-[11px] font-semibold rounded-card py-px px-1.5 min-w-[20px] text-center leading-[1.4] ${pendingTransmissions.includes(trans.name) ? 'text-primary bg-redSurface-100' : 'text-gray-400 bg-gray-100'}`}>{trans.count}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* 4. Fuel Type */}
              {searchFuelTypes.length > 0 && (
                <div className="mb-4 last:mb-0">
                  <button className="flex items-center justify-between w-full bg-none border-none cursor-pointer pb-2 [-webkit-tap-highlight-color:transparent]" onClick={() => toggleSection("fuel")}>
                    <h4 className="text-[13px] font-semibold text-gray-500 uppercase tracking-[0.4px] m-0">Fuel Type</h4>
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] font-medium text-primary cursor-pointer hover:underline" onClick={(e) => { e.stopPropagation(); setPendingFuelTypes(pendingFuelTypes.length === searchFuelTypes.length ? [] : searchFuelTypes.map((f) => f.name)); }}>
                        {pendingFuelTypes.length === searchFuelTypes.length ? "Deselect All" : "Select All"}
                      </span>
                      <svg className={`shrink-0 transition-transform duration-200 ${collapsedSections.fuel ? '-rotate-90' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M6 9l6 6 6-6" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </div>
                  </button>
                  {!collapsedSections.fuel && (
                    <div className="flex flex-wrap gap-2">
                      {searchFuelTypes.map((fuel) => (
                        <button key={fuel.name} className={`inline-flex items-center gap-1 py-1.5 px-3.5 rounded-card-lg border text-[13px] font-medium whitespace-nowrap cursor-pointer transition-[border-color,background,color] duration-[0.18s] [-webkit-tap-highlight-color:transparent]
                          ${pendingFuelTypes.includes(fuel.name) ? 'border-primary bg-redSurface-50 text-primary font-semibold' : 'border-gray-200 bg-bgPage text-gray-500 hover:border-gray-300 hover:bg-gray-100'}`} onClick={() => setPendingFuelTypes((prev) => prev.includes(fuel.name) ? prev.filter((f) => f !== fuel.name) : [...prev, fuel.name])}>
                          {fuel.name}<span className={`text-[11px] font-semibold rounded-card py-px px-1.5 min-w-[20px] text-center leading-[1.4] ${pendingFuelTypes.includes(fuel.name) ? 'text-primary bg-redSurface-100' : 'text-gray-400 bg-gray-100'}`}>{fuel.count}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* 5. Brand */}
              {searchBrands.length > 0 && (
                <div className="mb-4 last:mb-0">
                  <button className="flex items-center justify-between w-full bg-none border-none cursor-pointer pb-2 [-webkit-tap-highlight-color:transparent]" onClick={() => toggleSection("brand")}>
                    <h4 className="text-[13px] font-semibold text-gray-500 uppercase tracking-[0.4px] m-0">Brand</h4>
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] font-medium text-primary cursor-pointer hover:underline" onClick={(e) => { e.stopPropagation(); setPendingBrands(pendingBrands.length === searchBrands.length ? [] : searchBrands.map((b) => b.name)); }}>
                        {pendingBrands.length === searchBrands.length ? "Deselect All" : "Select All"}
                      </span>
                      <svg className={`shrink-0 transition-transform duration-200 ${collapsedSections.brand ? '-rotate-90' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M6 9l6 6 6-6" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </div>
                  </button>
                  {!collapsedSections.brand && (
                    <div className="flex flex-wrap gap-2">
                      {searchBrands.map((brand) => (
                        <button key={brand.name} className={`inline-flex items-center gap-1 py-1.5 px-3.5 rounded-card-lg border text-[13px] font-medium whitespace-nowrap cursor-pointer transition-[border-color,background,color] duration-[0.18s] [-webkit-tap-highlight-color:transparent]
                          ${pendingBrands.includes(brand.name) ? 'border-primary bg-redSurface-50 text-primary font-semibold' : 'border-gray-200 bg-bgPage text-gray-500 hover:border-gray-300 hover:bg-gray-100'}`} onClick={() => setPendingBrands((prev) => prev.includes(brand.name) ? prev.filter((b) => b !== brand.name) : [...prev, brand.name])}>
                          {brand.name}<span className={`text-[11px] font-semibold rounded-card py-px px-1.5 min-w-[20px] text-center leading-[1.4] ${pendingBrands.includes(brand.name) ? 'text-primary bg-redSurface-100' : 'text-gray-400 bg-gray-100'}`}>{brand.count}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

            </div>
            <div className={`flex items-center justify-between px-4 pt-2.5 pb-3.5 border-t border-gray-100 gap-2.5 ${bodyScrolled ? 'shadow-[0_-4px_12px_rgba(0,0,0,0.06)]' : ''}`}>
              <button
                className="flex-1 py-2.5 px-4 rounded-card text-sm font-semibold cursor-pointer transition-[background,opacity] duration-150 text-center bg-gray-100 text-gray-500 border border-gray-200 hover:bg-gray-200"
                onClick={() => {
                  setPendingAreas([]);
                  setPendingBrands([]);
                  setPendingFuelTypes([]);
                  setPendingTransmissions([]);
                  setPendingSortBy("");
                }}
              >
                Clear All
              </button>
              <button
                className="flex-1 py-2.5 px-4 rounded-card text-sm font-semibold cursor-pointer transition-[background,opacity] duration-150 text-center bg-primary text-white border-none hover:opacity-90"
                onClick={() => {
                  dispatch(setSelectedAreas(pendingAreas));
                  dispatch(setSelectedBrands(pendingBrands));
                  dispatch(setSelectedFuelTypes(pendingFuelTypes));
                  dispatch(setSelectedTransmissions(pendingTransmissions));
                  dispatch(setSortBy(pendingSortBy));

                  const startDTime = `${selectorData.startDateTime.date} ${selectorData.startDateTime.time}`;
                  const endDTime = `${selectorData.endDateTime.date} ${selectorData.endDateTime.time}`;
                  let apiData = {
                    types: multiSelect.filter((preState) => preState != "bike"),
                    start: startDTime,
                    end: endDTime,
                    cityId: selectorData.city.id,
                    platform: 3,
                  };
                  if (selectorData?.searchType == "/monthly-bike-rental") {
                    apiData.longRentStatus = 1;
                  }

                  const filters = {};
                  if (pendingAreas.length > 0) filters.areas = pendingAreas;
                  if (pendingBrands.length > 0) filters.brands = pendingBrands;
                  if (pendingFuelTypes.length > 0) filters.fuelTypes = pendingFuelTypes;
                  if (pendingTransmissions.length > 0) filters.transmissions = pendingTransmissions;
                  if (Object.keys(filters).length > 0) apiData.filters = filters;
                  if (pendingSortBy) apiData.sort = pendingSortBy;

                  dispatch(setIsSearching(true));
                  usefetchDataFromApi({
                    url: "search/index",
                    method: "POST",
                    data: withPartnerFilter(apiData, partner?.id),
                  }).then((res) => {
                    if (res.success) {
                      const { vehicles, filters: respFilters } = res.data?.data || {};
                      let finalVehicleList = getFinalVehicleList(vehicles, startDTime, endDTime);
                      dispatch(getAllVehicles(finalVehicleList));
                      // Always, filtered or not. Core::tallyFacets counts each
                      // facet with the other filters applied but not its own, so
                      // the options survive a selection and the counts track it.
                      // This used to be skipped whenever a filter was active,
                      // back when a filtered response listed only the value
                      // already chosen — which froze every count until a clear.
                      dispatchFilters(respFilters);
                    } else {
                      dispatch(getAllVehicles([]));
                    }
                    dispatch(setIsSearching(false));
                  });
                  setShowFilterPanel(false);
                }}
              >
                {vehicleCount > 0 ? `Show ${vehicleCount} vehicles` : "Apply"}
              </button>
            </div>
          </div>
        </>
      )}

      {selectorData?.searchType == "/monthly-bike-rental" && (
        <div className="week flex flex-wrap justify-center gap-2 mt-2 px-4 lg:w-3/5 lg:mx-auto lg:flex-nowrap lg:justify-evenly lg:gap-0 lg:px-0">
          {longTermDuration?.map((item, i) => {
            return (
              <span
                key={i}
                onClick={() => handleDuratnSelect(item)}
                className={`rounded-full px-4 py-2 text-sm lg:text-lg font-medium cursor-pointer whitespace-nowrap ${
                  selectorData?.duration?.value == item.value
                    ? "border border-slate-900 bg-bgGray100"
                    : "text-gray-500"
                }`}
              >
                {item.title}
              </span>
            );
          })}
        </div>
      )}

    </>
  );
};

export default NavigationBar;
